import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Building2, Car, Ship, Calendar, Sparkles, Star, Heart, CircleCheck, TrendingUp, CreditCard, Percent, Shield, Megaphone, Globe, ChevronDown, ChevronUp } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import FAQSection from "@/components/FAQSection";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HomeLeadSection from "@/components/HomeLeadSection";
import OfferSection from "@/components/OfferSection";
import HomeBlogSection from "@/components/HomeBlogSection";
import OptimizedImage from "@/components/OptimizedImage";
import ArtDecoFrame from "@/components/ArtDecoFrame";
import { useLanguage } from "@/contexts/LanguageContext";
import HandwrittenText from "@/components/HandwrittenText";
import heroCabassolPoster from "@/assets/hero-cabassol-poster.webp";
import heroVintageVilla from "@/assets/hero-vintage-villa.png";
import promoCabassol from "@/assets/promo-cabassol.mp4";
import videoServicesSequence from "@/assets/video-services-sequence.mp4";
import logoCabassol from "@/assets/logo-cabassol-2.webp";
import logoCabassolWhite from "@/assets/logo-cabassol-white.png";
import GoogleRatingBadge from "@/components/GoogleRatingBadge";
import AirbnbSuperhostBadge from "@/components/AirbnbSuperhostBadge";
import TestimonialsSection from "@/components/TestimonialsSection";
import { faqTranslations } from "@/locales/faq-translations";

// Import property images
import propertyVillaCapFerrat from "@/assets/property-villa-cap-ferrat.jpg";
import propertyBastideAix from "@/assets/property-bastide-aix.jpg";
import propertyAppartementCannes from "@/assets/property-appartement-cannes.jpg";
import propertyVillaEze from "@/assets/property-villa-eze-v2.jpg";
import propertyVillaValbonne from "@/assets/property-villa-valbonne.jpg";
import propertyVillaTropezienne from "@/assets/property-villa-tropezienne.jpg";
import propertyMasOliviers from "@/assets/property-mas-oliviers-v2.jpg";
import propertyVillaAntibes from "@/assets/property-villa-antibes-v3.jpg";
import propertyBastideAlpilles from "@/assets/property-bastide-alpilles-v3.jpg";
import propertyAppartementNice from "@/assets/property-appartement-nice.jpg";
import propertyMasSteVictoire from "@/assets/property-mas-ste-victoire-v2.jpg";
import propertyLoftVieuxNice from "@/assets/property-loft-vieux-nice.jpg";
import propertyVillaSteVictoire from "@/assets/property-villa-ste-victoire-v3.jpg";
import propertyBastideMignet from "@/assets/property-bastide-aix-centre.jpg";
import propertyAppartAixCentre from "@/assets/property-appart-aix-centre.jpg";
import propertyVillaCapAntibes from "@/assets/property-villa-cap-antibes-v2.jpg";
import propertyMasLuberon from "@/assets/property-mas-luberon-v2.jpg";
import propertyAppartSaintTropez from "@/assets/property-appartement-saint-tropez.jpg";
import propertyCabaneAframe from "@/assets/property-cabane-aframe-provence.jpg";
import propertyMasGordes from "@/assets/property-mas-gordes.jpg";
import propertyManoirMiremonts from "@/assets/property-manoir-miremonts.jpg";
import propertyCoconAix from "@/assets/property-cocon-aix-terrasse.jpg";

import serviceHomeGestion from "@/assets/service-home-gestion-v3.jpg";
import serviceHomeConciergerie from "@/assets/service-home-conciergerie-v4.jpg";
import serviceHomeAuto from "@/assets/service-home-auto.jpg";
import serviceHomeYacht from "@/assets/service-home-yacht-v3.jpg";
import serviceHomeExperiences from "@/assets/service-home-experiences-v4.jpg";
import serviceHomeStaff from "@/assets/service-home-staff.jpg";
import serviceHomeBilletterie from "@/assets/service-home-billetterie-v7.jpg";
import serviceHomeJet from "@/assets/service-home-jet-v9.jpg";
import serviceHomeMontres from "@/assets/service-home-montres-v14.jpg";

const serviceImages = {
  propertyManagement: serviceHomeGestion,
  concierge: serviceHomeConciergerie,
  automotive: serviceHomeAuto,
  yachting: serviceHomeYacht,
  events: serviceHomeExperiences,
  staff: serviceHomeStaff,
  ticketing: serviceHomeBilletterie,
  jet: serviceHomeJet,
  watches: serviceHomeMontres
};

const serviceIcons = {
  propertyManagement: Building2,
  concierge: Sparkles,
  automotive: Car,
  yachting: Ship,
  events: Calendar,
  staff: Sparkles,
  ticketing: Calendar,
  jet: Ship,
  watches: Sparkles
};

// Nos services (internes)
const ourServiceKeys = ["propertyManagement", "concierge", "staff"] as const;
// Services partenaires
const partnerServiceKeys = ["yachting", "ticketing", "automotive"] as const;

const partnerNames: Record<string, string> = {
  yachting: "Alexander",
  ticketing: "Antoine", 
  automotive: "Thomas"
};

// Detect in-app browsers (Instagram, Snapchat, Facebook, etc.)
const isInAppBrowser = (): boolean => {
  const ua = navigator.userAgent || navigator.vendor || (window as any).opera;
  // Check for common in-app browser signatures
  return /FBAN|FBAV|Instagram|Snapchat|Twitter|LinkedIn|Pinterest|TikTok|Line|WeChat|MicroMessenger/i.test(ua);
};

export default function Index() {
  const { t } = useLanguage();
  const { localePath, language } = useLocalizedPath();
  const heroVideoRef = useRef<HTMLVideoElement>(null);
  const servicesVideoRef = useRef<HTMLVideoElement>(null);
  const [heroVideoFailed, setHeroVideoFailed] = useState(false);
  const [servicesVideoFailed, setServicesVideoFailed] = useState(false);
  // heroVariant removed — single static hero title
  const [partnersExpanded, setPartnersExpanded] = useState(false);

  // Localized labels for the partners expand/collapse toggle (kept inline to avoid editing 11 locale files)
  const partnersToggleLabels: Record<string, { expand: string; collapse: string }> = {
    fr: { expand: "Découvrir nos services annexes", collapse: "Réduire" },
    en: { expand: "Discover our additional services", collapse: "Collapse" },
    es: { expand: "Descubrir nuestros servicios adicionales", collapse: "Reducir" },
    it: { expand: "Scopri i nostri servizi aggiuntivi", collapse: "Riduci" },
    de: { expand: "Unsere Zusatzleistungen entdecken", collapse: "Einklappen" },
    pt: { expand: "Descubra os nossos serviços adicionais", collapse: "Reduzir" },
    nl: { expand: "Ontdek onze aanvullende diensten", collapse: "Inklappen" },
    ru: { expand: "Откройте наши дополнительные услуги", collapse: "Свернуть" },
    jp: { expand: "付帯サービスを見る", collapse: "閉じる" },
    cn: { expand: "查看附加服务", collapse: "收起" },
    ar: { expand: "اكتشف خدماتنا الإضافية", collapse: "طي" },
  };
  const partnersToggle = partnersToggleLabels[language] || partnersToggleLabels.fr;





  // Detect in-app browsers synchronously so videos never mount there.
  const [isInApp] = useState<boolean>(() => isInAppBrowser());

  // Force autoplay on mobile devices with better error handling
  useEffect(() => {
    const playVideo = async (videoRef: React.RefObject<HTMLVideoElement>, onFail: () => void) => {
      if (!videoRef.current) return;
      
      try {
        await videoRef.current.play();
      } catch (error) {
        console.log('Video autoplay failed, showing fallback image');
        onFail();
      }
    };

    const playVideos = () => {
      playVideo(heroVideoRef, () => setHeroVideoFailed(true));
      playVideo(servicesVideoRef, () => setServicesVideoFailed(true));
    };

    // Delay slightly to ensure DOM is ready
    const timeout = setTimeout(playVideos, 100);

    // Also try on user interaction (required on some mobile browsers)
    const handleInteraction = () => {
      playVideos();
      document.removeEventListener('touchstart', handleInteraction);
      document.removeEventListener('click', handleInteraction);
    };

    document.addEventListener('touchstart', handleInteraction, { passive: true });
    document.addEventListener('click', handleInteraction);

    return () => {
      clearTimeout(timeout);
      document.removeEventListener('touchstart', handleInteraction);
      document.removeEventListener('click', handleInteraction);
    };
  }, []);

  const allProperties = [
    {
      name: "Bastide Aix Centre",
      location: "Aix-en-Provence",
      price: "650€",
      image: propertyBastideMignet,
      badge: "exclusive" as const
    },
    {
      name: "Villa des Cabassols",
      location: "Vauvenargues",
      price: "590€",
      image: propertyVillaSteVictoire,
      badge: "favorite" as const
    },
    {
      name: "Manoir de Miremonts",
      location: "Auriol",
      price: "490€",
      image: propertyManoirMiremonts,
      badge: "new" as const
    },
    {
      name: "Mas de Gordes",
      location: "Gordes",
      price: "850€",
      image: propertyMasGordes,
      badge: null
    },
    {
      name: "Cocon Aix Centre",
      location: "Aix-en-Provence",
      price: "80€",
      image: propertyCoconAix,
      badge: "new" as const
    },
    {
      name: "Mas des Oliviers",
      location: "Les Alpilles",
      price: "650€",
      image: propertyMasOliviers,
      badge: null
    },
    {
      name: "Appartement Aix Centre",
      location: "Aix-en-Provence",
      price: "120€",
      image: propertyAppartAixCentre,
      badge: "new" as const
    },
    {
      name: "Villa Cap d'Antibes",
      location: "Antibes",
      price: "1200€",
      image: propertyVillaCapAntibes,
      badge: "exclusive" as const
    },
    {
      name: "Mas du Luberon",
      location: "Luberon",
      price: "580€",
      image: propertyMasLuberon,
      badge: null
    },
    {
      name: "Appartement Saint-Tropez",
      location: "Saint-Tropez",
      price: "300€",
      image: propertyAppartSaintTropez,
      badge: "new" as const
    },
    {
      name: "Cabane A-Frame",
      location: "Luberon",
      price: "180€",
      image: propertyCabaneAframe,
      badge: "atypical" as const
    },
    {
      name: "Loft Vieux Nice",
      location: "Nice",
      price: "250€",
      image: propertyLoftVieuxNice,
      badge: "new" as const
    }
  ];

  const badgeLabels: Record<string, string> = {
    exclusive: (t as any).home?.badges?.exclusive || "Exclusif",
    new: (t as any).home?.badges?.new || "Nouveau",
    favorite: (t as any).home?.badges?.favorite || "Coup de cœur",
    atypical: (t as any).home?.badges?.atypical || "Atypique",
  };

  const BASE_URL = "https://conciergeriecabassol.fr";

  // JSON-LD Structured Data — WebSite + BreadcrumbList only
  // Organization & LocalBusiness are in index.html (static, canonical source)
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${BASE_URL}/#website`,
    "url": BASE_URL,
    "name": "Conciergerie Cabassol",
    "description": (t as any).seo?.home?.description || "Services lifestyle premium sur la Côte d'Azur",
    "publisher": {
      "@id": `${BASE_URL}/#organization`
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${BASE_URL}/${language}/apartments?search={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": t.nav.home,
        "item": `${BASE_URL}/${language}`
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": t.nav.services,
        "item": `${BASE_URL}/${language}/services`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": t.nav.apartments,
        "item": `${BASE_URL}/${language}/apartments`
      },
      {
        "@type": "ListItem",
        "position": 4,
        "name": t.nav.about,
        "item": `${BASE_URL}/${language}/about`
      },
      {
        "@type": "ListItem",
        "position": 5,
        "name": t.nav.contact,
        "item": `${BASE_URL}/${language}/contact`
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={(t as any).seo?.home?.title || "Conciergerie & Gestion Locative de Prestige"}
        description={(t as any).seo?.home?.description || "Services de conciergerie, gestion locative de biens immobiliers d'exception en Provence & Côte d'Azur."}
        keywords="conciergerie luxe, gestion locative Provence, gestion locative Côte d'Azur, villa prestige, yacht charter, Cannes, Nice, Saint-Tropez, Aix-en-Provence"
      />
      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <Navbar />
      
      <main id="main-content">
      {/* Hero Section */}
      <section id="hero" className="relative min-h-[100svh] flex items-center justify-center overflow-hidden pt-24 pb-20 md:pt-0 md:pb-0" aria-label="Hero - Services Lifestyle Premium">
        <div className="absolute inset-0">
          {/* Video with poster - autoPlay disabled in in-app browsers, controls hidden on mobile */}
          <video 
            ref={heroVideoRef}
            className="absolute inset-0 w-full h-full object-cover object-[center_30%] md:object-center [&::-webkit-media-controls]:hidden [&::-webkit-media-controls-enclosure]:hidden [&::-webkit-media-controls-panel]:hidden md:[&::-webkit-media-controls]:flex md:[&::-webkit-media-controls-enclosure]:flex md:[&::-webkit-media-controls-panel]:flex"
            autoPlay={!isInApp}
            loop 
            muted 
            playsInline
            controls={false}
            poster={heroCabassolPoster}
            onError={() => setHeroVideoFailed(true)}
          >
            <source src={promoCabassol} type="video/mp4" />
          </video>

          {/* Dark overlay - MUST be after video to appear on top */}
          <div className="absolute inset-0 bg-black/40 pointer-events-none" aria-hidden="true" />
        </div>
        
        <div className="relative z-10 text-center px-4 sm:px-6 max-w-5xl mx-auto">
          {/* Location mention */}
          <div className="flex items-center justify-center gap-3 sm:gap-4 mb-4 sm:mb-6 animate-fade-in" style={{ animationDelay: '0.2s', animationFillMode: 'both' }}>
            <div className="flex flex-col items-end gap-[3px]" aria-hidden="true">
              <span className="w-8 sm:w-12 h-px bg-gold/60" />
              <span className="w-6 sm:w-8 h-px bg-gold/60" />
              <span className="w-4 sm:w-5 h-px bg-gold/60" />
            </div>
            <p className="text-label text-white/60 text-[11px] sm:text-sm tracking-[0.15em] text-center">
              {(t as any).home?.location?.basedIn || "Conciergerie basée à"}<br className="sm:hidden" /> <span className="text-gold font-semibold tracking-[0.2em]">Aix-en-Provence</span>
            </p>
            <div className="flex flex-col items-start gap-[3px]" aria-hidden="true">
              <span className="w-8 sm:w-12 h-px bg-gold/60" />
              <span className="w-6 sm:w-8 h-px bg-gold/60" />
              <span className="w-4 sm:w-5 h-px bg-gold/60" />
            </div>
          </div>
          <h1 className="text-display text-4xl sm:text-5xl md:text-7xl lg:text-[5rem] text-white mb-4 sm:mb-5 animate-fade-in leading-[1.35]" style={{ animationDelay: '0.4s', animationFillMode: 'both' }}>
            <span className="block">{(t.hero as any).ownerTitle1}</span>
            <em className="block italic font-normal">{(t.hero as any).ownerTitle2}</em>
            <span className="block -mt-1 md:-mt-2">
              {t.hero.title3} <em className="italic font-normal">{t.hero.titleHighlight}</em>
            </span>
          </h1>



          <p className="text-white/80 text-base sm:text-lg md:text-xl lg:text-2xl mb-4 sm:mb-6 max-w-xs sm:max-w-lg md:max-w-2xl mx-auto animate-fade-in leading-relaxed px-2 sm:px-0" style={{ animationDelay: '0.6s', animationFillMode: 'both' }}>
            {t.hero.description}
          </p>
          <nav className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-4 sm:mb-6 animate-fade-in px-6 sm:px-0" style={{ animationDelay: '0.7s', animationFillMode: 'both' }} aria-label="Actions principales">
            <Button asChild className="bg-gold text-black hover:bg-gold/90 px-6 sm:px-8 py-4 sm:py-6 text-xs sm:text-sm tracking-wide sm:tracking-widest uppercase border-0 w-full sm:w-auto min-h-[48px]">
              <a href="?profile=owner#formulaire">{(t as any).home?.location?.estimateCta || "Demander une estimation"}</a>
            </Button>
            <Button asChild variant="outline" className="border border-white/80 text-white hover:bg-white hover:text-black px-6 sm:px-8 py-4 sm:py-6 text-xs sm:text-sm tracking-wide sm:tracking-widest uppercase bg-transparent w-full sm:w-auto min-h-[48px]">
              <a href="#formulaire">{(t as any).home?.location?.bookCta || "Réserver un hébergement"}</a>
            </Button>
          </nav>
          <div className="flex flex-wrap justify-center items-center gap-3 mb-6 sm:mb-10 md:mb-12 animate-fade-in" style={{ animationDelay: '0.8s', animationFillMode: 'both' }}>
            <GoogleRatingBadge className="border-white/20 bg-white/5 scale-[0.85] sm:scale-100" />
            <AirbnbSuperhostBadge className="border-white/20 bg-white/5 scale-[0.85] sm:scale-100" label={(t as any).home?.airbnbSuperhost || "Superhôte Airbnb"} />
          </div>
          <div className="w-full max-w-[280px] sm:max-w-md mx-auto animate-fade-in" style={{ animationDelay: '1s', animationFillMode: 'both' }}>
            {/* Partner cities - scrolling */}
            <div className="flex items-center justify-center gap-2 sm:gap-3 mb-2 sm:mb-3">
              <span className="text-white/50 text-[9px] sm:text-[11px] tracking-[0.15em] uppercase whitespace-nowrap">
                {(t as any).home?.location?.partnerNetwork || "Réseau de partenaires"}
              </span>
            </div>
            <div className="overflow-hidden">
              <div className="flex w-max animate-marquee">
                {[...Array(2)].map((_, groupIndex) => (
                  <div key={groupIndex} className="flex shrink-0 items-center gap-x-3 sm:gap-x-6 px-1.5 sm:px-3">
                    {["Aix-en-Provence", "Luberon", "Alpilles", "Verdon", "Côte d'Azur"].map((city) => (
                      <span key={`${groupIndex}-${city}`} className="flex items-center shrink-0">
                        <span className="text-white/60 text-[10px] sm:text-sm md:text-base tracking-wide whitespace-nowrap">{city}</span>
                        <span className="ml-3 sm:ml-6 w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-white/30" aria-hidden="true" />
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Scroll indicator - hidden on mobile */}
        <div className="absolute bottom-2 sm:bottom-3 left-1/2 -translate-x-1/2 text-white/50 hidden sm:block animate-scroll-bounce" aria-hidden="true">
          <div className="w-px h-6 sm:h-8 bg-gradient-to-b from-white/50 to-transparent mx-auto mb-1" />
          <span className="text-xs tracking-widest uppercase">{t.hero.scrollDown}</span>
        </div>
      </section>

      {/* Offer Section — Gestion complète & Prestations à la carte */}
      <OfferSection />

      {/* Our Services Section */}
      <section id="services-section" className="py-20 md:py-24 bg-background" aria-labelledby="services-heading">
        <div className="container">
          <header className="text-center mb-12">
            <div className="flex items-center justify-center gap-1.5 sm:gap-4 mb-4">
              <div className="flex flex-col items-end gap-[3px]" aria-hidden="true">
                <span className="w-12 h-px bg-gold/60" />
                <span className="w-8 h-px bg-gold/60" />
                <span className="w-5 h-px bg-gold/60" />
              </div>
              <p className="text-label text-gold">{t.services.subtitle}</p>
              <div className="flex flex-col items-start gap-[3px]" aria-hidden="true">
                <span className="w-12 h-px bg-gold/60" />
                <span className="w-8 h-px bg-gold/60" />
                <span className="w-5 h-px bg-gold/60" />
              </div>
            </div>
            <h2 id="services-heading" className="text-display text-4xl md:text-5xl lg:text-6xl text-foreground">
              {t.services.title}
            </h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
              {(t as any).home?.location?.servicesDescription || "Basés à Aix-en-Provence, nous opérons directement sur le Pays d'Aix et collaborons avec des conciergeries partenaires sur l'ensemble de la Provence & Côte d'Azur."}
            </p>
          </header>

          
          
          {/* Advantage Badges */}
          {(() => {
            const badges = (t as any).servicePages?.gestionLocative?.advantageBadges as string[] | undefined;
            const badgeIcons = [TrendingUp, CreditCard, Percent, Shield, Megaphone, Globe];
            return badges && badges.length > 0 ? (
              <div className="mx-auto mb-16 px-4 md:px-0">
                <div className="flex flex-wrap justify-center gap-2 md:gap-3">
                  {badges.map((label, i) => {
                    const BIcon = badgeIcons[i % badgeIcons.length];
                    return (
                      <div key={i} className="flex items-center gap-2 px-3 py-2 md:px-5 md:py-3 rounded-full border border-gold/30 bg-gold/5 whitespace-nowrap">
                        <BIcon className="w-4 h-4 text-gold flex-shrink-0" />
                        <span className="text-sm md:text-base text-foreground font-medium">{label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null;
          })()}

          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 list-none" role="list">
            {ourServiceKeys.map((key, index) => {
              const category = t.services.categories[key];
              const Icon = serviceIcons[key];
              const image = serviceImages[key];
              const keyPoints = (t as any).servicePages?.[key === "propertyManagement" ? "gestionLocative" : key === "concierge" ? "conciergerie" : key === "staff" ? "staff" : ""]?.homeKeyPoints as string[] | undefined;
              const hasDetailPage = key === "propertyManagement" || key === "concierge" || key === "staff";
              const detailPath = key === "propertyManagement" ? "/services/gestion-locative" : key === "concierge" ? "/services/conciergerie" : "/services/staff";
              
              return (
                <li 
                  key={key}
                  className="group relative overflow-hidden bg-card border border-border hover:border-gold/30 transition-all duration-700 flex flex-col"
                >
                  <article className="flex flex-col flex-1">
                    <div className="aspect-[4/3] overflow-hidden relative">
                      <ArtDecoFrame mode="inside" />
                      <OptimizedImage 
                        src={image} 
                        alt={`${category.title} - ${category.description}`}
                        width={400}
                        height={300}
                        sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                        className="image-elegant group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-black/25 pointer-events-none" />
                    </div>
                    <div className="p-8 flex flex-col flex-1">
                      <p className="text-label text-gold/80 mb-2">{category.subtitle}</p>
                      <h3 className="text-display text-2xl text-foreground mb-3">{category.title}</h3>
                      <p className="text-muted-foreground text-sm leading-relaxed mb-4">{category.description}</p>

                      {keyPoints && keyPoints.length > 0 && (
                        <ul className="space-y-1.5 mb-5">
                          {keyPoints.map((point, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm">
                              <CircleCheck className="w-4 h-4 text-gold flex-shrink-0" />
                              <span className="text-foreground">{point}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      
                      {hasDetailPage && (
                      <Button asChild variant="outline" size="sm" className="group/btn border-gold/30 text-gold hover:bg-gold hover:text-black mt-auto">
                          <Link to={localePath(detailPath)} aria-label={`${(t as any).servicePages?.learnMore || "En savoir plus"} – ${category.title}`}>
                            {(t as any).servicePages?.learnMore || "En savoir plus"}
                            <ArrowRight className="ml-1.5 w-3.5 h-3.5 group-hover/btn:translate-x-1 transition-transform" />
                          </Link>
                        </Button>
                      )}
                    </div>
                  </article>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* Cinematic Services Video Section — before Partner Services */}
      <section
        className={`relative w-full bg-background overflow-hidden transition-all duration-700 ease-elegant ${
          partnersExpanded
            ? "max-h-[75vh] opacity-100"
            : "max-h-0 opacity-0 pointer-events-none"
        }`}
        aria-label="Présentation vidéo des services"
        aria-hidden={!partnersExpanded}
      >
        <div className="absolute top-0 left-0 right-0 h-24 md:h-56 bg-gradient-to-b from-background via-background/60 md:via-background/80 to-transparent z-10 pointer-events-none" />
        <div className="absolute bottom-0 left-0 right-0 h-24 md:h-56 bg-gradient-to-t from-background via-background/60 md:via-background/80 to-transparent z-10 pointer-events-none" />
        <div className="relative w-full aspect-video max-h-[75vh] md:max-h-[60vh]">
          <video 
            ref={servicesVideoRef}
            className="w-full h-full object-cover [&::-webkit-media-controls]:hidden [&::-webkit-media-controls-enclosure]:hidden [&::-webkit-media-controls-panel]:hidden"
            autoPlay={!isInApp}
            loop 
            muted 
            playsInline
            controls={false}
            poster={heroCabassolPoster}
            onLoadedData={(e) => { (e.target as HTMLVideoElement).playbackRate = 1.0; }}
            onError={() => setServicesVideoFailed(true)}
            style={{ filter: 'sepia(0.15) saturate(0.85) brightness(0.95) contrast(1.05)' }}
          >
            <source src={videoServicesSequence} type="video/mp4" />
          </video>
          <div className="absolute inset-0 pointer-events-none opacity-20" style={{ 
            backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")',
            mixBlendMode: 'overlay'
          }} />
          <div className="absolute inset-0 bg-black/10 md:bg-black/25 pointer-events-none" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 md:from-black/40 via-transparent to-black/15 md:to-black/30 pointer-events-none" />
          <div className="absolute inset-0 pointer-events-none hidden md:block" style={{
            background: 'radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.4) 100%)'
          }} />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
            <img 
              src={logoCabassol} 
              alt="Conciergerie Cabassol" 
              width="128"
              height="128"
              loading="eager"
              decoding="async"
              className="w-20 sm:w-28 md:w-40 lg:w-48 opacity-90"
              style={{ aspectRatio: '1 / 1' }}
            />
          </div>
        </div>
      </section>

      {/* Partner Services Section */}
      <section
        className={`bg-background transition-all duration-500 ease-elegant ${
          partnersExpanded ? "pt-0 pb-20 md:pb-24" : "pt-2 pb-10"
        }`}
        aria-labelledby="partner-services-heading"
      >
        <div className="container">
          <header
            className={`text-center transition-all duration-500 ease-elegant ${
              partnersExpanded ? "mb-20" : "mb-8"
            }`}
          >
            <div className="flex items-center justify-center gap-1.5 sm:gap-4 mb-4">
              <div className="flex flex-col items-end gap-[3px]" aria-hidden="true">
                <span className="w-12 h-px bg-gold/60" />
                <span className="w-8 h-px bg-gold/60" />
                <span className="w-5 h-px bg-gold/60" />
              </div>
              <p className="text-label text-gold">{t.services.partnerSubtitle}</p>
              <div className="flex flex-col items-start gap-[3px]" aria-hidden="true">
                <span className="w-12 h-px bg-gold/60" />
                <span className="w-8 h-px bg-gold/60" />
                <span className="w-5 h-px bg-gold/60" />
              </div>
            </div>
            <h2 id="partner-services-heading" className="text-display text-4xl md:text-5xl lg:text-6xl text-foreground">
              {t.services.partnerTitle}
            </h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">{t.services.partnerDescription}</p>
          </header>
          
          <div className="relative">
            <ul
              id="partner-services-list"
              className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 list-none transition-all duration-700 ease-elegant ${
                partnersExpanded
                  ? "max-h-[3000px] blur-0 opacity-100"
                  : "max-h-[260px] overflow-hidden opacity-85 pointer-events-none select-none [filter:blur(3px)]"
              }`}
              role="list"
              aria-hidden={!partnersExpanded}
            >
              {partnerServiceKeys.map((key, index) => {
                const category = t.services.categories[key];
                const Icon = serviceIcons[key];
                const image = serviceImages[key];
                const partnerName = partnerNames[key];

                return (
                  <li
                    key={key}
                    className="group relative overflow-hidden bg-card border border-border hover:border-gold/30 transition-all duration-700"
                  >
                    <article>
                      <div className="aspect-[4/3] overflow-hidden relative">
                        <ArtDecoFrame mode="inside" />
                        <OptimizedImage
                          src={image}
                          alt={`${category.title} - ${category.description}`}
                          width={400}
                          height={300}
                          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                          className="image-elegant group-hover:scale-105 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-black/10 pointer-events-none" />
                      </div>
                      <div className="p-8">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-label text-gold/80">{category.subtitle}</p>
                          <span className="text-xs text-muted-foreground italic">{(t as any).home?.location?.withPartner || "avec"} {partnerName}</span>
                        </div>
                        <h3 className="text-display text-2xl text-foreground mb-3">{category.title}</h3>
                        <p className="text-muted-foreground text-sm leading-relaxed">{category.description}</p>
                      </div>
                    </article>
                  </li>
                );
              })}
            </ul>

            {!partnersExpanded && (
              <div
                className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-background via-background/70 to-transparent pointer-events-none"
                aria-hidden="true"
              />
            )}
          </div>

          <div className="flex justify-center mt-8">
            <Button
              variant="outline"
              onClick={() => setPartnersExpanded((v) => !v)}
              aria-expanded={partnersExpanded}
              aria-controls="partner-services-list"
              className="gap-2 text-xs tracking-widest uppercase font-medium px-6 py-2.5"
            >
              {partnersExpanded ? partnersToggle.collapse : partnersToggle.expand}
              {partnersExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </section>

      <TestimonialsSection />

      {/* Properties Section */}
      <section className="py-20 md:py-24 bg-background overflow-hidden" aria-labelledby="properties-heading">
        <div className="container mb-16">
          <header className="text-center mb-0">
            <div className="flex items-center justify-center gap-1.5 sm:gap-4 mb-4">
              <div className="flex flex-col items-end gap-[3px]" aria-hidden="true">
                <span className="w-12 h-px bg-gold/60" />
                <span className="w-8 h-px bg-gold/60" />
                <span className="w-5 h-px bg-gold/60" />
              </div>
              <p className="text-label text-gold">{t.properties.subtitle}</p>
              <div className="flex flex-col items-start gap-[3px]" aria-hidden="true">
                <span className="w-12 h-px bg-gold/60" />
                <span className="w-8 h-px bg-gold/60" />
                <span className="w-5 h-px bg-gold/60" />
              </div>
            </div>
            <h2 id="properties-heading" className="text-display text-4xl md:text-5xl lg:text-6xl text-foreground">
              {t.properties.title}
            </h2>
          </header>
        </div>
        
        {/* Single row - scrolling left to right */}
        <div className="relative w-full overflow-hidden marquee-container">
          {/* Fade overlay left */}
          <div className="absolute left-0 top-0 bottom-0 w-24 md:w-40 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
          {/* Fade overlay right */}
          <div className="absolute right-0 top-0 bottom-0 w-24 md:w-40 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />
          
          <div className="flex animate-marquee-slow">
            {[...Array(2)].map((_, groupIndex) => (
              <div key={groupIndex} className="flex shrink-0 gap-8 px-4">
                {allProperties.map((property) => (
                  <article key={`${groupIndex}-${property.name}`} className="group shrink-0 w-[280px] md:w-[320px]">
                    <div className="relative aspect-[4/3] overflow-hidden mb-4 border border-transparent group-hover:border-gold/20 transition-all duration-500">
                      <ArtDecoFrame mode="inside" color="white" />
                      {property.badge && (
                        <span className={`absolute top-4 left-1/2 -translate-x-1/2 z-20 px-3 py-1.5 text-xs font-medium tracking-wider uppercase backdrop-blur-md flex items-center gap-1.5 ${
                          property.badge === "exclusive" 
                            ? "bg-gold/20 text-gold border border-gold/30" 
                            : property.badge === "new" 
                              ? "bg-foreground/20 text-foreground border border-foreground/30" 
                              : "bg-destructive/20 text-destructive border border-destructive/30"
                        }`}>
                          {property.badge === "exclusive" && <Star className="h-3 w-3" />}
                          {property.badge === "new" && <Sparkles className="h-3 w-3" />}
                          {property.badge === "favorite" && <Heart className="h-3 w-3" />}
                          {badgeLabels[property.badge]}
                        </span>
                      )}
                      <OptimizedImage 
                        src={property.image} 
                        alt={`${property.name} - Villa de luxe à ${property.location}`}
                        width={800}
                        height={600}
                        sizes="400px"
                        className="image-elegant group-hover:scale-105 transition-transform duration-700 blur-[1.5px] group-hover:blur-0"
                      />
                      <div className="absolute inset-0 bg-black/25 pointer-events-none" />
                      {/* Logo overlay */}
                      <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                        <img 
                          src={logoCabassolWhite} 
                          alt="" 
                          className="w-20 md:w-24 opacity-60 group-hover:opacity-80 transition-opacity duration-500 drop-shadow-lg" 
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-display text-lg text-foreground group-hover:text-gold transition-colors">{property.name}</h3>
                        <p className="text-muted-foreground text-sm">{property.location}</p>
                      </div>
                      <p className="text-foreground font-medium" aria-label={`Prix: ${property.price} par nuit`}>
                        {property.price}<span className="text-muted-foreground text-sm">{t.properties.perNight}</span>
                      </p>
                    </div>
                  </article>
                ))}
              </div>
            ))}
          </div>
        </div>

      </section>

      {/* Lead Form Section */}
      <section id="formulaire" className="relative py-16 md:py-20 overflow-hidden" style={{ background: 'linear-gradient(180deg, hsl(225 22% 13%) 0%, hsl(228 20% 11%) 50%, hsl(225 22% 13%) 100%)' }} aria-labelledby="lead-form-heading">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.04]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, hsl(var(--gold)) 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
        
        {/* Subtle gold glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] rounded-full pointer-events-none opacity-[0.04]" style={{
          background: 'radial-gradient(ellipse, hsl(var(--gold)) 0%, transparent 70%)'
        }} />
        
        {/* Top Art Deco border */}
        <div className="absolute top-0 left-0 right-0 h-8 pointer-events-none" aria-hidden="true">
          {/* Main gold line */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gold/25" />
          {/* Second thinner line */}
          <div className="absolute top-[4px] left-8 right-8 md:left-16 md:right-16 h-px bg-gold/15" />
          {/* Third finest line */}
          <div className="absolute top-[8px] left-16 right-16 md:left-32 md:right-32 h-px bg-gold/10" />
          {/* Corner accents - top left */}
          <div className="absolute top-0 left-0 w-6 h-6">
            <span className="absolute top-0 left-0 w-6 h-px bg-gold/40" />
            <span className="absolute top-0 left-0 h-6 w-px bg-gold/40" />
          </div>
          {/* Corner accents - top right */}
          <div className="absolute top-0 right-0 w-6 h-6">
            <span className="absolute top-0 right-0 w-6 h-px bg-gold/40" />
            <span className="absolute top-0 right-0 h-6 w-px bg-gold/40" />
          </div>
        </div>
        
        <div className="container max-w-5xl mx-auto relative z-10">
          <header className="text-center mb-12">
            <div className="flex items-center justify-center gap-1.5 sm:gap-4 mb-4">
              <div className="flex flex-col items-end gap-[3px]" aria-hidden="true">
                <span className="w-12 h-px bg-gold/60" />
                <span className="w-8 h-px bg-gold/60" />
                <span className="w-5 h-px bg-gold/60" />
              </div>
              <p className="text-label text-gold">{t.contact.subtitle}</p>
              <div className="flex flex-col items-start gap-[3px]" aria-hidden="true">
                <span className="w-12 h-px bg-gold/60" />
                <span className="w-8 h-px bg-gold/60" />
                <span className="w-5 h-px bg-gold/60" />
              </div>
            </div>
            <h2 id="lead-form-heading" className="text-display text-4xl md:text-5xl lg:text-6xl text-foreground">
              {t.contact.sendMessage}
            </h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
              {(t as any).home?.location?.leadFormDescription || "Voyageur, propriétaire ou simple curiosité — décrivez-nous votre besoin et recevez une réponse personnalisée sous 24h."}
            </p>
            
            {/* Trust indicators */}
            <div className="flex flex-wrap items-center justify-center gap-6 mt-8">
              <span className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider">
                <CircleCheck className="w-4 h-4 text-gold" />
                {(t as any).home?.location?.available7days || "Disponible 7j/7"}
              </span>
              <span className="w-px h-4 bg-border hidden sm:block" />
              <span className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider">
                <CircleCheck className="w-4 h-4 text-gold" />
                {(t as any).home?.location?.noCommitment || "Sans engagement"}
              </span>
              <span className="w-px h-4 bg-border hidden sm:block" />
              <span className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider">
                <CircleCheck className="w-4 h-4 text-gold" />
                {(t as any).home?.location?.response24h || "Réponse sous 24h"}
              </span>
            </div>
            <div className="flex justify-center mt-4">
              <GoogleRatingBadge variant="inline" />
            </div>
          </header>
          <HomeLeadSection />
        </div>
        
        {/* Bottom Art Deco border */}
        <div className="absolute bottom-0 left-0 right-0 h-8 pointer-events-none" aria-hidden="true">
          {/* Main gold line */}
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gold/25" />
          {/* Second thinner line */}
          <div className="absolute bottom-[4px] left-8 right-8 md:left-16 md:right-16 h-px bg-gold/15" />
          {/* Third finest line */}
          <div className="absolute bottom-[8px] left-16 right-16 md:left-32 md:right-32 h-px bg-gold/10" />
          {/* Corner accents - bottom left */}
          <div className="absolute bottom-0 left-0 w-6 h-6">
            <span className="absolute bottom-0 left-0 w-6 h-px bg-gold/40" />
            <span className="absolute bottom-0 left-0 h-6 w-px bg-gold/40" />
          </div>
          {/* Corner accents - bottom right */}
          <div className="absolute bottom-0 right-0 w-6 h-6">
            <span className="absolute bottom-0 right-0 w-6 h-px bg-gold/40" />
            <span className="absolute bottom-0 right-0 h-6 w-px bg-gold/40" />
          </div>
        </div>
      </section>


      <FAQSection showCta={false} />

      {/* Blog Section */}
      <HomeBlogSection />

      {/* Contact CTA */}
      <section className="pb-16 md:pb-20 bg-background" aria-label={language === "fr" ? "Nous contacter" : "Contact us"}>
        <div className="container text-center border-t border-border/40 pt-12">
          <p className="text-muted-foreground mb-2">{(faqTranslations[language] || faqTranslations.fr).anotherQuestion}</p>
          <Link
            to={localePath("/contact")}
            className="inline-flex items-center gap-2 btn-primary"
          >
            {(faqTranslations[language] || faqTranslations.fr).contactUs} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
      </main>

      <Footer />
    </div>
  );
}
